(() => {
  const script = document.currentScript;
  const config = {
    facetsSrc: script?.dataset.facetsSrc || '',
    commerceSrc: script?.dataset.commerceSrc || '',
  };

  if (window.CBHFacetsRuntimeLoader?.register) {
    window.CBHFacetsRuntimeLoader.register(config);
    return;
  }

  const sources = { ...config };
  const scriptPromises = new Map();
  let runtimePromise = null;

  const register = (next = {}) => {
    Object.entries(next).forEach(([key, value]) => {
      if (value && !sources[key]) sources[key] = value;
    });
  };

  const absoluteUrl = (src) => {
    try {
      return new URL(src, window.location.href).href;
    } catch (error) {
      return src;
    }
  };

  const loadScript = (src) => {
    if (!src) return Promise.resolve();

    const url = absoluteUrl(src);
    if (scriptPromises.has(url)) return scriptPromises.get(url);

    const promise = new Promise((resolve, reject) => {
      const existing = Array.from(document.scripts).find((node) => node.src === url);
      if (existing?.dataset.cbhRuntimeLoaded === 'true') {
        resolve();
        return;
      }

      const node = existing || document.createElement('script');
      const onLoad = () => {
        node.dataset.cbhRuntimeLoaded = 'true';
        resolve();
      };
      const onError = () => reject(new Error(`Failed to load ${url}`));

      node.addEventListener('load', onLoad, { once: true });
      node.addEventListener('error', onError, { once: true });

      if (!existing) {
        node.src = url;
        node.async = true;
        node.dataset.cbhRuntimeInjected = 'true';
        document.head.appendChild(node);
      }
    });

    scriptPromises.set(url, promise);
    return promise;
  };

  const facetsReady = () => Boolean(customElements.get('facet-filters-form'));
  const commerceReady = () => !sources.commerceSrc || window.CBHMainCollectionCommerce === true;
  const runtimeReady = () => facetsReady() && commerceReady();

  const loadRuntime = () => {
    if (runtimeReady()) return Promise.resolve();
    runtimePromise ??= Promise.all([
      facetsReady() ? Promise.resolve() : loadScript(sources.facetsSrc),
      commerceReady() ? Promise.resolve() : loadScript(sources.commerceSrc),
    ]).then(() => {
      if (!facetsReady()) throw new Error('Facet runtime did not initialise.');
    });
    return runtimePromise;
  };

  const withinFacets = (target) => Boolean(target?.closest?.('[data-cbh-facets-root]'));
  const warmFromTarget = (target) => {
    if (withinFacets(target)) loadRuntime().catch(() => {});
  };

  document.addEventListener('pointerover', (event) => warmFromTarget(event.target), { passive: true });
  document.addEventListener('focusin', (event) => warmFromTarget(event.target));
  document.addEventListener('touchstart', (event) => warmFromTarget(event.target), { passive: true });

  const replayEvent = (target, type) => {
    const replay = new Event(type, { bubbles: true, cancelable: true });
    replay.cbhRuntimeReplay = true;
    target.dispatchEvent(replay);
  };

  ['input', 'change'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        if (event.cbhRuntimeReplay || runtimeReady() || !withinFacets(event.target)) return;

        event.stopImmediatePropagation();
        const target = event.target;
        loadRuntime()
          .then(() => replayEvent(target, type))
          .catch((error) => window.cbhLogError?.('Facet runtime failed:', error));
      },
      true
    );
  });

  document.addEventListener(
    'click',
    (event) => {
      if (runtimeReady()) return;

      const action = event.target?.closest?.(
        '[data-cbh-mobile-sort-open], [data-cbh-close-facets], facet-remove a'
      );
      if (!action || !withinFacets(action)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      loadRuntime()
        .then(() => action.click())
        .catch((error) => {
          window.cbhLogError?.('Facet action runtime failed:', error);

          if (action.matches('facet-remove a') && action.href) {
            window.location.assign(action.href);
            return;
          }

          if (action.matches('[data-cbh-close-facets]')) {
            const form = action.closest('form');
            if (form) HTMLFormElement.prototype.submit.call(form);
            return;
          }

          if (action.matches('[data-cbh-mobile-sort-open]')) {
            const root = action.closest('[data-cbh-facets-root]');
            root?.querySelector('details.mobile-facets__disclosure > summary')?.click();
          }
        });
    },
    true
  );

  /* =========================================================
     TECH2G — WCAG 2.2 AA facet hardening
     - 2.1.4: neutralise the legacy global "/" character shortcut.
       Normal Tab navigation to the labelled facet-search field remains.
     - 2.4.3 / 4.1.2: expose the mobile filter surface as a modal dialog
       and scope focus to the active nested filter while it is open.
     - 1.4.11 / 2.4.7: use an opaque, high-contrast keyboard focus ring.
  ========================================================= */
  const isTypingTarget = (target) =>
    Boolean(target?.matches?.('input, textarea, select, [contenteditable="true"]'));

  const ensureFacetFocusStyle = () => {
    if (document.getElementById('cbh-tech2g-facet-focus-style')) return;

    const style = document.createElement('style');
    style.id = 'cbh-tech2g-facet-focus-style';
    style.textContent = `
      .cbh-facets :is(a, button, summary, select, input, [role='button']):focus-visible {
        outline: 2px solid var(--cbh-facet-green, #2f5d46) !important;
        outline-offset: 3px !important;
      }
    `;
    document.head.appendChild(style);
  };

  const syncNestedFacetFocusScope = (drawer) => {
    const main = drawer?.querySelector?.('.mobile-facets__main');
    if (!main) return;

    const nestedDetails = Array.from(
      main.querySelectorAll(':scope > details.mobile-facets__details[data-cbh-mobile-facet]')
    );
    const openDetails = nestedDetails.find((details) => details.open) || null;

    Array.from(main.children).forEach((child) => {
      child.removeAttribute('inert');
      child.querySelector?.(':scope > summary')?.removeAttribute('inert');

      if (!openDetails) return;

      if (child === openDetails) {
        child.querySelector?.(':scope > summary')?.setAttribute('inert', '');
      } else {
        child.setAttribute('inert', '');
      }
    });
  };

  const hardenMobileFacetDrawer = (drawer) => {
    const details = drawer?.querySelector?.('details.mobile-facets__disclosure');
    const summary = details?.querySelector?.(':scope > summary');
    const panel = details?.querySelector?.('.mobile-facets__inner');
    if (!details || !summary || !panel) return;

    if (!panel.id) {
      panel.id = `${details.id || 'MobileFacets'}-dialog`;
    }

    panel.setAttribute('data-menu-drawer', '');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');

    const heading = Array.from(panel.querySelectorAll('.mobile-facets__heading')).find(
      (node) => String(node.textContent || '').trim()
    );
    const label =
      String(heading?.textContent || '').trim() ||
      String(summary.textContent || '').trim() ||
      'Filter and sort';

    panel.setAttribute('aria-label', label);
    summary.setAttribute('aria-haspopup', 'dialog');
    summary.setAttribute('aria-controls', panel.id);

    syncNestedFacetFocusScope(drawer);
  };

  const hardenFacetAccessibility = (root = document) => {
    ensureFacetFocusStyle();
    root
      .querySelectorAll?.('[data-cbh-facets-root] menu-drawer.mobile-facets__wrapper')
      .forEach(hardenMobileFacetDrawer);
  };

  /*
   * WCAG 2.1.4 Character Key Shortcuts:
   * facets.js historically listened for "/" on document to move focus to
   * facet search. Suppress that content-level shortcut before it reaches
   * document while leaving browser/default behaviour untouched.
   */
  window.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== '/' || event.defaultPrevented || isTypingTarget(event.target)) return;

      const visibleFacetSearch = Array.from(document.querySelectorAll('[data-cbh-facet-input]')).find(
        (input) => input.offsetParent !== null
      );
      if (!visibleFacetSearch) return;

      event.stopPropagation();
    },
    true
  );

  document.addEventListener(
    'toggle',
    (event) => {
      const target = event.target;
      if (!target?.matches?.('details.mobile-facets__details[data-cbh-mobile-facet]')) return;
      const drawer = target.closest('menu-drawer.mobile-facets__wrapper');
      if (drawer) window.requestAnimationFrame(() => syncNestedFacetFocusScope(drawer));
    },
    true
  );

  document.addEventListener('shopify:section:load', (event) => {
    window.requestAnimationFrame(() => hardenFacetAccessibility(event.target || document));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hardenFacetAccessibility(), { once: true });
  } else {
    hardenFacetAccessibility();
  }

  window.CBHFacetsRuntimeLoader = {
    register,
    loadRuntime,
  };
})();
